import React, { useEffect, useState, useRef } from 'react';
import { ChevronDownIcon, XIcon, ShieldAlertIcon, CheckCircleIcon, SearchIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { ToggleSwitch } from './ToggleSwitch';
import { genres, subgenres, tropes, themes, characters, settings, contentWarnings, representation, culturalIdentities, relationshipTypes, spiceLevels, burnLevels, darknessLevels, endingTypes, povTypes, tenseTypes } from '../../data/bookTags';
interface ContentFilterSectionProps {
  allowlistEnabled: boolean;
  onAllowlistToggle: (enabled: boolean) => void;
  allowlistTags: string[];
  onAllowlistChange: (tags: string[]) => void;
  blocklistTags: string[];
  onBlocklistChange: (tags: string[]) => void;
}
const authors = ['Sarah J. Maas', 'Colleen Hoover', 'Rebecca Yarros', 'Ana Huang', 'Emily Henry', 'Ali Hazelwood', 'Penelope Douglas', 'L.J. Shen', 'Tessa Bailey', 'Hannah Grace', 'Elle Kennedy', 'Elsie Silver'];
const preferredFormats = ['eBook', 'Paperback', 'Hardcover', 'Audiobook'];
export const ContentFilterSection: React.FC<ContentFilterSectionProps> = ({
  allowlistEnabled,
  onAllowlistToggle,
  allowlistTags,
  onAllowlistChange,
  blocklistTags,
  onBlocklistChange
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  // Grouped categories — ordered: experience types up top, then content tags, then people
  const categories = [{
    id: 'genres',
    name: 'Genres',
    options: genres.map(g => g.name)
  }, {
    id: 'subgenres',
    name: 'Subgenres',
    options: subgenres.map(s => s.name)
  }, {
    id: 'spiceLevels',
    name: 'Spice Levels',
    options: spiceLevels.map(s => s.name)
  }, {
    id: 'burnLevels',
    name: 'Burn Levels',
    options: burnLevels.map(b => b.name)
  }, {
    id: 'darknessLevels',
    name: 'Darkness Levels',
    options: darknessLevels.map(d => d.name)
  }, {
    id: 'endingTypes',
    name: 'Ending Types',
    options: endingTypes.map(e => e.name)
  }, {
    id: 'povTypes',
    name: 'POV Types',
    options: povTypes.map(p => p.name)
  }, {
    id: 'tenseTypes',
    name: 'Tense Types',
    options: tenseTypes.map(t => t.name)
  }, {
    id: 'pairingTypes',
    name: 'Pairing Types',
    options: relationshipTypes.map(r => r.name)
  }, {
    id: 'preferredFormat',
    name: 'Preferred Format',
    options: preferredFormats
  }, {
    id: 'tropes',
    name: 'Tropes',
    options: tropes.map(t => t.name)
  }, {
    id: 'themes',
    name: 'Themes',
    options: themes.map(t => t.name)
  }, {
    id: 'characters',
    name: 'Characters',
    options: characters.map(c => c.name)
  }, {
    id: 'settings',
    name: 'Settings',
    options: settings.map(s => s.name)
  }, {
    id: 'representation',
    name: 'Representation',
    options: representation.map(r => r.name)
  }, {
    id: 'culturalIdentity',
    name: 'Cultural Identity',
    options: culturalIdentities.map(c => c.name)
  }, {
    id: 'contentWarnings',
    name: 'Content Warnings',
    options: contentWarnings.map(c => c.name)
  }, {
    id: 'authors',
    name: 'Authors',
    options: authors
  }];
  const allSearchableItems = categories.flatMap(cat => cat.options.map(option => ({
    name: option,
    categoryName: cat.name,
    categoryId: cat.id
  })));
  const handleTagToggle = (tag: string, listType: 'allow' | 'block') => {
    if (listType === 'allow') {
      if (allowlistTags.includes(tag)) {
        onAllowlistChange(allowlistTags.filter(t => t !== tag));
      } else {
        onAllowlistChange([...allowlistTags, tag]);
        if (blocklistTags.includes(tag)) {
          onBlocklistChange(blocklistTags.filter(t => t !== tag));
        }
      }
    } else {
      if (blocklistTags.includes(tag)) {
        onBlocklistChange(blocklistTags.filter(t => t !== tag));
      } else {
        onBlocklistChange([...blocklistTags, tag]);
        if (allowlistTags.includes(tag)) {
          onAllowlistChange(allowlistTags.filter(t => t !== tag));
        }
      }
    }
  };
  const UnifiedSearchBar = ({
    listType
  }: {
    listType: 'allow' | 'block';
  }) => {
    const [query, setQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const selectedList = listType === 'allow' ? allowlistTags : blocklistTags;
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);
    const filteredResults = allSearchableItems.filter(item => item.name.toLowerCase().includes(query.toLowerCase()) && !selectedList.includes(item.name)).slice(0, 10);
    return <div className="relative mb-6" ref={wrapperRef} data-id="element-554" data-mp="1mzk8wdy87odf">
        <div className="relative" data-id="element-555" data-mp="q8s3qbctzgqf">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-556" data-mp="q9jm2khf3hyr">
            <SearchIcon className={`h-4 w-4 ${dk.textMuted}`} data-id="element-557" data-mp="v8nzxofae3vf" />
          </div>
          <input type="text" className={`block w-full pl-10 pr-3 py-2 border rounded-md leading-5 bg-transparent placeholder-gray-500 focus:outline-none focus:ring-2 sm:text-sm transition-colors ${dk.isDark ? 'border-gray-600 text-white focus:border-indigo-500 focus:ring-indigo-500' : 'border-gray-300 text-gray-900 focus:border-indigo-500 focus:ring-indigo-500'} ${rt.inputFocus}`} placeholder="Search genres, tropes, authors, tags..." value={query} onChange={e => {
          setQuery(e.target.value);
          setIsOpen(true);
        }} onFocus={() => setIsOpen(true)} data-id="element-558" data-mp="z6lpiscyyrzc" />
        </div>

        {isOpen && query.length > 0 && <div className={`absolute z-10 mt-1 w-full rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 overflow-hidden border ${dk.border}`} data-id="element-559" data-mp="khfhdr9kc57e">
            <ul className="max-h-60 overflow-auto py-1 text-base sm:text-sm" data-id="element-560" data-mp="3ny4dejbirwit">
              {filteredResults.length > 0 ? filteredResults.map((item, index) => <li key={`${item.categoryId}-${item.name}-${index}`} className={`cursor-pointer select-none relative py-2 pl-3 pr-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`} onClick={() => {
            handleTagToggle(item.name, listType);
            setQuery('');
            setIsOpen(false);
          }} data-id="element-561" data-mp="1rfr511zkk2e" data-mpi={index}>
                    <div className="flex items-center justify-between" data-id="element-562" data-mp="1bra56jwel6et">
                      <span className={`font-medium truncate ${dk.textHeading}`} data-id="element-563" data-mp="1fsbxegrwki7a">
                        {item.name}
                      </span>
                      <span className={`text-xs ml-2 flex-shrink-0 ${dk.textMuted}`} data-id="element-564" data-mp="23g3hw7nlk1jn">
                        {item.categoryName}
                      </span>
                    </div>
                  </li>) : <li className={`cursor-default select-none relative py-2 pl-3 pr-4 ${dk.textMuted}`} data-id="element-565" data-mp="1zifjgump3qn0">
                  No matching tags found
                </li>}
            </ul>
          </div>}
      </div>;
  };
  const CategoryAccordion = ({
    category,
    listType
  }: {
    category: (typeof categories)[0];
    listType: 'allow' | 'block';
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    const selectedList = listType === 'allow' ? allowlistTags : blocklistTags;
    const selectedCount = category.options.filter(opt => selectedList.includes(opt)).length;
    return <div className={`border rounded-lg overflow-hidden mb-3 ${dk.border}`} data-id="element-566" data-mp="1ydxd10k76avz">
        <button type="button" onClick={() => setIsOpen(!isOpen)} className={`w-full flex items-center justify-between p-4 text-left transition-colors ${dk.surfaceMuted}`} data-id="element-567" data-mp="1tfvtyfmrubki">
          <div className="flex items-center gap-3" data-id="element-568" data-mp="1jpau6ovd3100">
            <span className={`font-medium ${dk.textHeading}`} data-id="element-569" data-mp="2h5q3oobsnk0j">
              {category.name}
            </span>
            {selectedCount > 0 && <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${listType === 'allow' ? rt.badge : 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300'}`} data-id="element-570" data-mp="2ce83i2hlhngm">
                {selectedCount} selected
              </span>}
          </div>
          <ChevronDownIcon className={`h-5 w-5 transition-transform duration-200 ${dk.textMuted} ${isOpen ? 'rotate-180' : ''}`} data-id="element-571" data-mp="2tmr65ewolavd" />
        </button>

        {isOpen && <div className={`p-4 border-t ${dk.border} ${dk.card}`} data-id="element-572" data-mp="32o8utlo5mkvo">
            <div className="flex flex-wrap gap-2" data-id="element-573" data-mp="37nldrvbwn7sz">
              {category.options.map(option => {
            const isSelected = selectedList.includes(option);
            let chipClass = `border ${dk.border} ${dk.textSecondary} hover:bg-black/5 dark:hover:bg-white/5`;
            if (isSelected) {
              if (listType === 'allow') {
                chipClass = `${rt.bgLight} ${rt.textPrimary} ${rt.borderActive}`;
              } else {
                chipClass = 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-700/50';
              }
            }
            return <button key={option} type="button" onClick={() => handleTagToggle(option, listType)} className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${chipClass}`} data-id="element-574" data-mp="1jiwvfo0j8iin">
                    {option}
                    {isSelected && <XIcon className="ml-1.5 h-3.5 w-3.5 opacity-70 hover:opacity-100" data-id="element-575" data-mp="22968jsxuxup4" />}
                  </button>;
          })}
            </div>
          </div>}
      </div>;
  };
  return <div className="space-y-8" data-id="element-576" data-mp="7x7xrmjwgqr4">
      {/* Allowlist Section */}
      <div data-id="element-577" data-mp="cu8849ns8fo1">
        <div className="flex items-start justify-between mb-4" data-id="element-578" data-mp="cvt0ptqjdoj9">
          <div className="pr-4" data-id="element-579" data-mp="5b8pw7c24u69">
            <div className="flex items-center gap-2 mb-1" data-id="element-580" data-mp="5ca9x1b1wr04">
              <CheckCircleIcon className={`h-5 w-5 ${rt.textPrimary}`} data-id="element-581" data-mp="kijqrvt4nai" />
              <h4 className={`text-base font-semibold ${dk.textHeading}`} data-id="element-582" data-mp="j7bofvbwtdl">
                "Only Show Me" (Allowlist)
              </h4>
            </div>
            <p className={`text-sm ${dk.textMuted}`} data-id="element-583" data-mp="3msi35r4a6992">
              When enabled, your browse, recommendations, and discovery will{' '}
              <strong data-id="element-584" data-mp="o9biew3td98t">only</strong> show books matching these tags.
            </p>
          </div>
          <div className="mt-1" data-id="element-585" data-mp="jbjibet0mgtj">
            <ToggleSwitch name="allowlistEnabled" checked={allowlistEnabled} onChange={e => onAllowlistToggle(e.target.checked)} data-id="element-586" data-mp="ebx00qybyadv" />
          </div>
        </div>

        {allowlistEnabled && <div className="mt-4 animate-in fade-in slide-in-from-top-2 duration-300" data-id="element-587" data-mp="2z2xh2zctyvvk">
            <UnifiedSearchBar listType="allow" data-id="element-588" data-mp="2u202ewr14jyt" />
            <div className="space-y-1" data-id="element-589" data-mp="2u5e5663luv6s">
              {categories.map(category => <CategoryAccordion key={`allow-${category.id}`} category={category} listType="allow" data-id="element-590" data-mp="1m1huqpmj1nfp" />)}
            </div>
          </div>}
      </div>

      {/* Blocklist Section */}
      <div className="pt-6 border-t border-gray-200 dark:border-gray-700" data-id="element-591" data-mp="1h37w251lg4pc">
        <div className="mb-4" data-id="element-592" data-mp="12fd3qpri02cs">
          <div className="flex items-center gap-2 mb-1" data-id="element-593" data-mp="12e23v116beqy">
            <ShieldAlertIcon className="h-5 w-5 text-amber-500" data-id="element-594" data-mp="176v7flkwoh1g" />
            <h4 className={`text-base font-semibold ${dk.textHeading}`} data-id="element-595" data-mp="1786afj6j1kri">
              "Hide From My Feed" (Blocklist)
            </h4>
          </div>
          <p className={`text-sm ${dk.textMuted}`} data-id="element-596" data-mp="yf2yjfbj5fyz">
            Books tagged with these won't appear in your browse,
            recommendations, or discovery. Direct searches and shared links are
            not affected.
          </p>
        </div>

        <UnifiedSearchBar listType="block" data-id="element-597" data-mp="ohoivhxfkv7q" />
        <div className="space-y-1" data-id="element-598" data-mp="4v3i3olv94ph">
          {categories.map(category => <CategoryAccordion key={`block-${category.id}`} category={category} listType="block" data-id="element-599" data-mp="syogpvf482u" />)}
        </div>
      </div>
    </div>;
};